import { Component, Input, OnInit } from '@angular/core';
import { EcommerceService } from 'src/app/services/ecommerce.service';
import { BreeceService } from 'src/app/services/breece.service';
import { ImageUrlService } from 'src/app/services/image-url.service';
import { NbDialogService } from '@nebular/theme';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';
import { ProductFormDialogComponent } from './product-form-dialog/product-form-dialog.component';

@Component({
  selector: 'app-product-management',
  templateUrl: './product-management.component.html',
  styleUrls: ['./product-management.component.scss']
})
export class ProductManagementComponent implements OnInit {

  @Input() storeId: string = '';
  @Input() storeConfig: any;

  products: any[] = [];
  categories: any[] = [];
  loading: boolean = false;

  // Pagination
  currentPage: number = 1;
  totalPages: number = 1;
  totalProducts: number = 0;
  pageSize: number = 20;

  // Filters
  searchQuery: string = '';
  selectedCategory: string = '';
  selectedStatus: string = '';
  minPrice: number = 0;
  maxPrice: number = 0;
  placementAisle: string = '';
  placementShelf: string = '';
  sortBy: string = 'createdAt';
  sortOrder: string = 'desc';

  // UI State
  showFilters: boolean = false;
  selectedProducts: Set<string> = new Set();
  bulkAction: string = '';

  // Store Configuration
  storeCurrency: string = 'DKK';
  skuRequirementEnabled: boolean = false;
  catalogImportDefaultCategoryId: string = '';

  // Breece ESL Integration
  breeceEnabled = false;
  breeceBulkSyncing = false;
  syncingProductIds = new Set<string>();

  // Deposit settings from store
  depositSettings: any = {
    enabled: false,
    categoryA: 1.00,
    categoryB: 1.50,
    categoryC: 3.00
  };
  depositFeeTypes: any[] = [];
  depositAutoAssignByEAN: boolean = false;

  // Make Math available in template
  Math = Math;

  constructor(
    private ecommerceService: EcommerceService,
    private breeceService: BreeceService,
    private imageUrlService: ImageUrlService,
    private dialogService: NbDialogService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) { }

  ngOnInit(): void {
    if (this.storeId) {
      this.loadStoreSettings().then(() => {
        this.loadCategories();
        this.loadProducts();
      });
    }
  }

  loadProducts(): void {
    this.loading = true;

    const params = {
      page: this.currentPage,
      limit: this.pageSize,
      sortBy: this.sortBy,
      sortOrder: this.sortOrder,
      adminView: true,
      ...(this.searchQuery && { search: this.searchQuery }),
      ...(this.selectedCategory && { category: this.selectedCategory }),
      ...(this.minPrice && { minPrice: this.minPrice }),
      ...(this.maxPrice && { maxPrice: this.maxPrice }),
      ...(this.placementAisle && { placementAisle: this.placementAisle }),
      ...(this.placementShelf && { placementShelf: this.placementShelf }),
      ...(this.selectedStatus && { inStock: this.selectedStatus === 'instock' })
    };

    console.log('Loading products with params:', params);

    this.ecommerceService.getStoreProducts(this.storeId, params).subscribe(
      (res: any) => {
        console.log('Products API response:', res);

        if (res.response && res.response.success && res.response.data) {
          const data = res.response.data;
          this.products = data.products || [];

          const pagination = data.pagination;
          if (pagination) {
            this.totalProducts = pagination.totalProducts || 0;
            this.totalPages = pagination.totalPages || 1;
            this.currentPage = pagination.currentPage || 1;

            console.log('Pagination data:', {
              currentPage: this.currentPage,
              totalPages: this.totalPages,
              totalProducts: this.totalProducts,
              productsLoaded: this.products.length
            });
          }
        } else if (res.code === 200 && res.data) {
          this.products = res.data.products || [];
          this.totalProducts = res.data.pagination?.totalProducts || 0;
          this.totalPages = res.data.pagination?.totalPages || 1;
          this.currentPage = res.data.pagination?.currentPage || 1;
        } else {
          console.warn('Unexpected products response format:', res);
          this.products = [];
          this.totalProducts = 0;
          this.totalPages = 1;
          this.currentPage = 1;
        }
        this.loading = false;
      },
      (error) => {
        console.error('Failed to load products:', error);
        this.toastr.error('Failed to load products');
        this.products = [];
        this.totalProducts = 0;
        this.totalPages = 1;
        this.currentPage = 1;
        this.loading = false;
      }
    );
  }

  loadCategories(): void {
    this.ecommerceService.getStoreCategories(this.storeId, { adminView: 'true' }).subscribe(
      (res: any) => {
        console.log('Categories API response (product management):', res);

        if (res.response && res.response.success && res.response.data) {
          this.categories = res.response.data.categories || [];
          console.log('Categories loaded for product dropdown:', this.categories);
        } else if (res.code === 200 && res.data) {
          this.categories = res.data.categories || [];
        } else {
          console.warn('Unexpected categories response format:', res);
          this.categories = [];
        }
      },
      (error) => {
        console.error('Failed to load categories:', error);
        this.toastr.error('Failed to load categories');
      }
    );
  }

  exportProductsCSV(): void {
    this.spinner.show();
    this.ecommerceService.exportProducts(this.storeId).subscribe(
      (response: any) => {
        this.spinner.hide();
        const products = response?.data?.products || response?.response?.data?.products || [];
        if (!products.length) {
          this.toastr.warning('No products to export');
          return;
        }

        const headers = [
          'Name', 'SKU', 'Barcode', 'Category', 'Price', 'Original Price',
          'Cost Price', 'Tax Rate', 'Tax Included', 'Status', 'Visible',
          'Featured', 'Quantity', 'Low Stock Threshold', 'Track Inventory',
          'Allow Backorder', 'Age Restricted', 'Minimum Age', 'Has Deposit',
          'Deposit Quantity', 'Has Consumption Limit', 'Exclude From Loyalty Discount',
          'Description', 'Created At'
        ];

        const escapeCSV = (val: any): string => {
          if (val === null || val === undefined) return '';
          const str = String(val);
          if (str.includes(',') || str.includes('"') || str.includes('\n')) {
            return `"${str.replace(/"/g, '""')}"`;
          }
          return str;
        };

        const rows = products.map((p: any) => [
          p.name, p.sku, p.barcode, p.category, p.price, p.originalPrice,
          p.costPrice, p.taxRate, p.taxIncluded, p.status, p.isVisible,
          p.isFeatured, p.quantity, p.lowStockThreshold, p.trackInventory,
          p.allowBackorder, p.ageRestricted, p.minimumAge, p.hasDeposit,
          p.depositQuantity, p.hasConsumptionLimit, p.excludeFromLoyaltyDiscount,
          p.description, p.createdAt
        ].map(escapeCSV).join(','));

        const csvContent = [headers.join(','), ...rows].join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `products-export-${this.storeId}-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        window.URL.revokeObjectURL(url);
        this.toastr.success(`${products.length} products exported successfully`);
      },
      (error: any) => {
        this.spinner.hide();
        this.toastr.error('Failed to export products');
      }
    );
  }

  onSearch(): void {
    this.currentPage = 1;
    this.loadProducts();
  }

  onFilterChange(): void {
    this.currentPage = 1;
    this.loadProducts();
  }

  onSortChange(): void {
    this.loadProducts();
  }

  onPageChange(page: number): void {
    console.log('Page change requested:', page, 'Current page was:', this.currentPage);
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadProducts();
    }
  }

  getPaginationPages(): number[] {
    const pages: number[] = [];
    const maxVisiblePages = 5;

    if (this.totalPages <= maxVisiblePages) {
      for (let i = 1; i <= this.totalPages; i++) {
        pages.push(i);
      }
    } else {
      let start = Math.max(2, this.currentPage - 2);
      let end = Math.min(this.totalPages - 1, this.currentPage + 2);

      if (this.currentPage <= 3) {
        start = 2;
        end = Math.min(this.totalPages - 1, 5);
      } else if (this.currentPage >= this.totalPages - 2) {
        start = Math.max(2, this.totalPages - 4);
        end = this.totalPages - 1;
      }

      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
    }

    return pages;
  }

  toggleFilters(): void {
    this.showFilters = !this.showFilters;
  }

  clearFilters(): void {
    this.searchQuery = '';
    this.selectedCategory = '';
    this.selectedStatus = '';
    this.minPrice = 0;
    this.maxPrice = 0;
    this.placementAisle = '';
    this.placementShelf = '';
    this.onFilterChange();
  }

  openAddProductDialog(): void {
    this.dialogService.open(ProductFormDialogComponent, {
      context: {
        storeId: this.storeId,
        product: null,
        categories: this.categories,
        storeCurrency: this.storeCurrency,
        skuRequirementEnabled: this.skuRequirementEnabled,
        depositSettings: this.depositSettings,
        depositFeeTypes: this.depositFeeTypes,
        autoAssignByEAN: this.depositAutoAssignByEAN,
        catalogImportDefaultCategoryId: this.catalogImportDefaultCategoryId
      },
      hasBackdrop: true,
      closeOnBackdropClick: false
    }).onClose.subscribe((result: any) => {
      if (result?.saved) {
        this.loadProducts();
      }
    });
  }

  editProduct(product: any): void {
    this.dialogService.open(ProductFormDialogComponent, {
      context: {
        storeId: this.storeId,
        product: product,
        categories: this.categories,
        storeCurrency: this.storeCurrency,
        skuRequirementEnabled: this.skuRequirementEnabled,
        depositSettings: this.depositSettings,
        depositFeeTypes: this.depositFeeTypes,
        autoAssignByEAN: this.depositAutoAssignByEAN
      },
      hasBackdrop: true,
      closeOnBackdropClick: false
    }).onClose.subscribe((result: any) => {
      if (result?.saved) {
        this.loadProducts();
      }
    });
  }

  deleteProduct(productId: string): void {
    Swal.fire({
      title: 'Are you sure?',
      text: 'This will permanently delete the product',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545'
    }).then((result) => {
      if (result.isConfirmed) {
        this.performDeleteProduct(productId);
      }
    });
  }

  performDeleteProduct(productId: string): void {
    this.spinner.show();
    this.ecommerceService.deleteProduct(this.storeId, productId).subscribe(
      (res: any) => {
        console.log('Delete product response:', res);
        if ((res.response && res.response.success) || res.code === 200) {
          this.toastr.success('Product deleted successfully');
          this.loadProducts();
        }
        this.spinner.hide();
      },
      (error) => {
        console.error('Failed to delete product:', error);
        this.toastr.error('Failed to delete product: ' + (error.error?.message || error.message));
        this.spinner.hide();
      }
    );
  }

  toggleProductSelection(productId: string): void {
    if (this.selectedProducts.has(productId)) {
      this.selectedProducts.delete(productId);
    } else {
      this.selectedProducts.add(productId);
    }
  }

  selectAllProducts(): void {
    if (this.selectedProducts.size === this.products.length) {
      this.selectedProducts.clear();
    } else {
      this.products.forEach(product => {
        this.selectedProducts.add(product._id);
      });
    }
  }

  executeBulkAction(): void {
    if (!this.bulkAction || this.selectedProducts.size === 0) {
      this.toastr.warning('Please select products and an action');
      return;
    }

    const selectedProductIds = Array.from(this.selectedProducts);

    switch (this.bulkAction) {
      case 'delete':
        this.bulkDeleteProducts(selectedProductIds);
        break;
      case 'enable':
        this.bulkToggleVisibility(selectedProductIds, true);
        break;
      case 'disable':
        this.bulkToggleVisibility(selectedProductIds, false);
        break;
      default:
        this.toastr.info('Bulk action will be implemented');
    }
  }

  bulkDeleteProducts(productIds: string[]): void {
    Swal.fire({
      title: 'Delete Multiple Products?',
      text: `This will delete ${productIds.length} products permanently`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete them!',
      cancelButtonText: 'Cancel',
      confirmButtonColor: '#dc3545'
    }).then((result) => {
      if (result.isConfirmed) {
        // TODO: Implement bulk delete API call
        this.toastr.info('Bulk delete will be implemented');
      }
    });
  }

  bulkToggleVisibility(productIds: string[], isVisible: boolean): void {
    // TODO: Implement bulk visibility toggle
    this.toastr.info('Bulk visibility toggle will be implemented');
  }

  toggleProductStatus(product: any, newChecked: boolean): void {
    const newStatus = newChecked ? 1 : 0;
    console.log('[TOGGLE-STATUS] called', { productId: product._id, currentStatus: product.status, newChecked, newStatus });

    const before = this.products.find(p => p._id === product._id);
    this.products = this.products.map(p =>
      p._id === product._id ? { ...p, status: newStatus } : p
    );
    const after = this.products.find(p => p._id === product._id);
    console.log('[TOGGLE-STATUS] optimistic update', { idMatched: before !== after || after?.status === newStatus, before: before?.status, after: after?.status });

    this.ecommerceService.updateProduct(this.storeId, product._id, {
      status: newStatus
    }).subscribe(
      (res: any) => {
        console.log('[TOGGLE-STATUS] HTTP response', res);
        console.log('[TOGGLE-STATUS] res?.response?.success =', res?.response?.success);
        if (res?.response?.success) {
          console.log('[TOGGLE-STATUS] SUCCESS branch — keeping new state');
          this.toastr.success(
            `Product ${newStatus === 1 ? 'activated' : 'deactivated'} successfully`
          );
        } else {
          console.log('[TOGGLE-STATUS] ELSE branch — REVERTING');
          this.products = this.products.map(p =>
            p._id === product._id ? { ...p, status: newStatus === 1 ? 0 : 1 } : p
          );
        }
      },
      (error: any) => {
        console.log('[TOGGLE-STATUS] ERROR branch — REVERTING', error);
        this.products = this.products.map(p =>
          p._id === product._id ? { ...p, status: newStatus === 1 ? 0 : 1 } : p
        );
        this.toastr.error('Failed to update product status');
      }
    );
  }

  toggleProductVisibility(product: any, newVisibility: boolean): void {
    console.log('[TOGGLE-VIS] called', { productId: product._id, currentIsVisible: product.isVisible, newVisibility });

    const before = this.products.find(p => p._id === product._id);
    this.products = this.products.map(p =>
      p._id === product._id ? { ...p, isVisible: newVisibility } : p
    );
    const after = this.products.find(p => p._id === product._id);
    console.log('[TOGGLE-VIS] optimistic update', { idMatched: before !== after || after?.isVisible === newVisibility, before: before?.isVisible, after: after?.isVisible });

    this.ecommerceService.updateProduct(this.storeId, product._id, {
      isVisible: newVisibility
    }).subscribe(
      (res: any) => {
        console.log('[TOGGLE-VIS] HTTP response', res);
        console.log('[TOGGLE-VIS] res?.response?.success =', res?.response?.success);
        if (res?.response?.success) {
          console.log('[TOGGLE-VIS] SUCCESS branch — keeping new state');
          this.toastr.success(
            `Product ${newVisibility ? 'visible' : 'hidden'} successfully`
          );
        } else {
          console.log('[TOGGLE-VIS] ELSE branch — REVERTING');
          this.products = this.products.map(p =>
            p._id === product._id ? { ...p, isVisible: !newVisibility } : p
          );
        }
      },
      (error) => {
        console.log('[TOGGLE-VIS] ERROR branch — REVERTING', error);
        this.products = this.products.map(p =>
          p._id === product._id ? { ...p, isVisible: !newVisibility } : p
        );
        this.toastr.error('Failed to update product visibility');
      }
    );
  }

  toggleProductFeatured(product: any): void {
    const newFeatured = !product.isFeatured;

    this.ecommerceService.updateProduct(this.storeId, product._id, {
      isFeatured: newFeatured
    }).subscribe(
      (res: any) => {
        if (res.code === 200) {
          product.isFeatured = newFeatured;
          this.toastr.success(
            `Product ${newFeatured ? 'featured' : 'unfeatured'} successfully`
          );
        }
      },
      (error) => {
        console.error('Failed to update product featured status:', error);
        this.toastr.error('Failed to update product status');
      }
    );
  }

  formatCurrency(amount: number): string {
    if (!amount) return '0.00';

    const currencyLocaleMap: { [key: string]: string } = {
      'DKK': 'da-DK',
      'EUR': 'de-DE',
      'USD': 'en-US',
      'GBP': 'en-GB',
      'SEK': 'sv-SE',
      'NOK': 'nb-NO'
    };

    const locale = currencyLocaleMap[this.storeCurrency] || 'da-DK';
    const currency = this.storeCurrency || 'DKK';

    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currency
    }).format(amount);
  }

  getStockStatusColor(status: string): string {
    switch (status) {
      case 'instock': return 'success';
      case 'lowstock': return 'warning';
      case 'outofstock': return 'danger';
      default: return 'basic';
    }
  }

  getCategoryName(categoryId: any): string {
    if (typeof categoryId === 'object' && categoryId !== null) {
      return categoryId.name || 'Uncategorized';
    } else if (typeof categoryId === 'string') {
      const category = this.categories.find(cat => cat._id === categoryId);
      return category ? category.name : 'Uncategorized';
    }
    return 'Uncategorized';
  }

  trackByProductId(index: number, product: any): string {
    return product._id;
  }

  getProductImageUrl(product: any): string {
    return this.imageUrlService.getProductImageUrl(product);
  }

  onImageError(event: any): void {
    const imgElement = event.target as HTMLImageElement;
    if (imgElement) {
      imgElement.style.display = 'none';
    }
  }

  loadStoreSettings(): Promise<void> {
    return new Promise((resolve) => {
      const role = localStorage.getItem('role') || '';
      const settingsReq = this.ecommerceService.getStoreSettings(this.storeId);
      const feeTypesReq = role === 'manager'
        ? this.ecommerceService.getPosDepositFeeTypes(this.storeId)
        : this.ecommerceService.getDepositFeeTypes();

      Promise.all([
        settingsReq.toPromise().catch(() => {
          // Fallback to storeConfig from parent (works for managers who get 403)
          if (this.storeConfig?.depositSettings) {
            return this.storeConfig;
          }
          return null;
        }),
        feeTypesReq.toPromise().catch(() => null)
      ]).then(([settingsRes, feeTypesRes]: any[]) => {
        if (settingsRes) {
          let store: any = null;
          if (settingsRes.code === 200 && settingsRes.data?.store) {
            store = settingsRes.data.store;
          } else if (settingsRes.response?.success && settingsRes.response?.data?.store) {
            store = settingsRes.response.data.store;
          } else if (settingsRes.depositSettings) {
            // Fallback: raw store object from parent storeConfig input
            store = settingsRes;
          }
          if (store) {
            this.storeCurrency = store.currency || 'DKK';
            this.skuRequirementEnabled = store.skuRequirementEnabled || false;
            this.catalogImportDefaultCategoryId = store.catalogImportDefaultCategoryId || '';
            this.breeceEnabled = store.breeceConfig?.enabled || false;
            if (store.depositSettings) {
              this.depositSettings = {
                enabled: store.depositSettings.enabled || false,
                autoAssignByEAN: store.depositSettings.autoAssignByEAN || false,
                categoryA: store.depositSettings.categoryA || 1.00,
                categoryB: store.depositSettings.categoryB || 1.50,
                categoryC: store.depositSettings.categoryC || 3.00
              };
              this.depositAutoAssignByEAN = store.depositSettings.autoAssignByEAN || false;
            }
          }
        }
        if (feeTypesRes) {
          if (role === 'manager') {
            // POS endpoint returns { depositEnabled, feeTypes: [...] } (may be wrapped in envelope)
            const feeTypes = feeTypesRes?.response?.data?.feeTypes
              ?? feeTypesRes?.data?.feeTypes
              ?? feeTypesRes?.feeTypes
              ?? [];
            this.depositFeeTypes = Array.isArray(feeTypes) ? feeTypes.filter((ft: any) => ft.active) : [];
          } else {
            const rawTypes = feeTypesRes?.response?.data ?? feeTypesRes?.data ?? feeTypesRes;
            this.depositFeeTypes = Array.isArray(rawTypes) ? rawTypes.filter((ft: any) => ft.active) : [];
          }
        }
        resolve();
      });
    });
  }

  getDepositAmount(category: string): number {
    if (!category || !this.depositSettings.enabled) return 0;
    switch (category) {
      case 'A': return this.depositSettings.categoryA;
      case 'B': return this.depositSettings.categoryB;
      case 'C': return this.depositSettings.categoryC;
      default: return 0;
    }
  }

  getDepositDisplayText(product: any): string {
    if (!product.hasDeposit || !product.depositCategory) return '';
    const amount = this.getDepositAmount(product.depositCategory);
    return `${this.formatCurrency(amount)} deposit`;
  }

  getConsumptionLimitDisplay(product: any): string {
    if (!product.hasConsumptionLimit || !product.consumptionLimit) return '';
    const { limitType, maxQuantity } = product.consumptionLimit;
    return `${maxQuantity}/${limitType}`;
  }

  getLockedFieldsCount(product: any): number {
    if (!product?.fieldLocks) return 0;
    const locks = product.fieldLocks instanceof Map
      ? Array.from((product.fieldLocks as Map<string, boolean>).values())
      : Object.values(product.fieldLocks);
    return (locks as boolean[]).filter((v: boolean) => v === true).length;
  }

  syncAllToBreece(): void {
    if (!confirm('This will sync all active products to Breece electronic shelf labels. Continue?')) return;
    this.breeceBulkSyncing = true;
    this.breeceService.bulkSync(this.storeId).subscribe({
      next: (res: any) => {
        this.breeceBulkSyncing = false;
        const count = res?.response?.data?.syncedCount ?? '?';
        this.toastr.success(`${count} products synced to Breece`);
      },
      error: (err: any) => {
        this.breeceBulkSyncing = false;
        this.toastr.error(err?.error?.response?.message || err?.error?.message || 'Breece sync failed');
      }
    });
  }

  syncSelectedToBreece(): void {
    const ids = Array.from(this.selectedProducts);
    if (ids.length === 0) return;
    if (!confirm(`Sync ${ids.length} selected product(s) to Breece ESLs?`)) return;
    this.breeceBulkSyncing = true;
    this.breeceService.syncSelected(this.storeId, ids).subscribe({
      next: (res: any) => {
        const count = res?.response?.data?.syncedCount ?? ids.length;
        this.toastr.success(`${count} products synced to Breece`);
        this.breeceBulkSyncing = false;
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.response?.message || 'Breece sync failed');
        this.breeceBulkSyncing = false;
      }
    });
  }

  createSelectedInBreece(): void {
    const ids = Array.from(this.selectedProducts);
    if (ids.length === 0) return;
    if (!confirm(`Create ${ids.length} selected product(s) in Breece?`)) return;
    this.breeceBulkSyncing = true;
    this.breeceService.createSelected(this.storeId, ids).subscribe({
      next: (res: any) => {
        this.toastr.success(`${ids.length} products created in Breece`);
        this.breeceBulkSyncing = false;
      },
      error: (err: any) => {
        this.toastr.error(err?.error?.response?.message || 'Breece create failed');
        this.breeceBulkSyncing = false;
      }
    });
  }

  syncProductToBreece(productId: string): void {
    this.syncingProductIds.add(productId);
    this.breeceService.syncProduct(this.storeId, productId).subscribe({
      next: () => {
        this.syncingProductIds.delete(productId);
        this.toastr.success('Product synced to Breece');
      },
      error: (err: any) => {
        this.syncingProductIds.delete(productId);
        this.toastr.error(err?.error?.message || 'Failed to sync product');
      }
    });
  }
}
